import { useState, useRef, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Check } from 'lucide-react';
import { useAuthStore } from 'store/authStore';
import { useWebSocket } from 'hooks/useWebSocket';
import type { UserRole } from 'types';

const ROLE_LABELS: Record<string, string> = {
  lawyer: 'Юридический отдел',
  hr: 'HR-отдел',
  ops: 'Операционный менеджер',
  admin: 'Администратор',
};

const SWITCHABLE_USERS: Array<{ role: UserRole; name: string; initials: string }> = [
  { role: 'admin', name: 'Адам', initials: 'АД' },
  { role: 'hr', name: 'Герман', initials: 'ГР' },
  { role: 'lawyer', name: 'Юрий', initials: 'ЮР' },
  { role: 'ops', name: 'Пётр', initials: 'ПЁ' },
];

export default function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const switchTo = useAuthStore((s) => s.switchTo);
  const navigate = useNavigate();
  useWebSocket(user?.id ?? null);
  const location = useLocation();
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [switchingRole, setSwitchingRole] = useState<UserRole | null>(null);
  const avatarRef = useRef<HTMLDivElement>(null);

  const handleSwitch = async (role: UserRole) => {
    if (switchingRole || role === user?.role) return;
    setSwitchingRole(role);
    try {
      await switchTo(role);
      setAvatarOpen(false);
    } finally {
      setSwitchingRole(null);
    }
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!user) return null;

  const tabs = [
    { label: 'Сценарии', path: '/scenarios' },
    { label: 'Источники данных', path: '/sources' },
    { label: 'Ассистент', path: '/agent' },
    { label: 'Дашборд', path: '/dashboard' },
    ...(user.role === 'admin' ? [{ label: 'Администрирование', path: '/admin' }] : []),
  ];

  const currentTab = tabs.find((t) => location.pathname.startsWith(t.path));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        bgcolor: 'transparent',
      }}
    >
      {/* ── Header ── */}
      <Box
        component="header"
        sx={{
          height: 54,
          background: 'rgba(255,255,255,0.86)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderBottom: '1px solid rgba(20,116,255,0.08)',
          boxShadow: '0 1px 0 rgba(20,116,255,0.06), 0 4px 20px rgba(20,116,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          px: 3,
          gap: 2,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            flexShrink: 0,
          }}
          onClick={() => navigate('/scenarios')}
        >
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: '7px',
              background: 'linear-gradient(135deg, #1474FF 0%, #3B8BFF 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 10px rgba(20,116,255,0.35)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <circle cx="6.5" cy="6.5" r="2.5" fill="white" fillOpacity="0.95" />
              <circle cx="6.5" cy="2" r="1.3" fill="white" fillOpacity="0.5" />
              <circle cx="6.5" cy="11" r="1.3" fill="white" fillOpacity="0.5" />
              <circle cx="2" cy="6.5" r="1.3" fill="white" fillOpacity="0.5" />
              <circle cx="11" cy="6.5" r="1.3" fill="white" fillOpacity="0.5" />
            </svg>
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 14.5,
              color: 'var(--color-text-primary)',
              letterSpacing: '-0.4px',
              lineHeight: 1,
            }}
          >
            Globus
            <Box component="span" sx={{ color: 'var(--color-primary)', fontWeight: 800 }}>
              {' '}
              AI
            </Box>
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={{ width: '1px', height: 18, bgcolor: 'var(--color-border)', flexShrink: 0 }} />

        {/* Navigation tabs */}
        <Box
          sx={{
            display: 'flex',
            gap: '2px',
            background: 'rgba(20,116,255,0.06)',
            border: '1px solid rgba(20,116,255,0.12)',
            borderRadius: '9px',
            p: '3px',
          }}
        >
          {tabs.map((tab) => {
            const isActive = currentTab?.path === tab.path;
            return (
              <Box
                key={tab.path}
                onClick={() => navigate(tab.path)}
                sx={{
                  px: 1.75,
                  py: '5px',
                  borderRadius: '6px',
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                  background: isActive ? 'rgba(255,255,255,0.92)' : 'transparent',
                  boxShadow: isActive ? '0 1px 4px rgba(20,116,255,0.14)' : 'none',
                  transition: 'all 0.15s ease',
                  userSelect: 'none',
                  letterSpacing: '-0.01em',
                  '&:hover': {
                    color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                    background: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.60)',
                  },
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>

        {/* Right section */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Avatar / user menu */}
          <Box ref={avatarRef} sx={{ position: 'relative' }}>
            <Box
              onClick={() => setAvatarOpen((v) => !v)}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #07111F 0%, #1D3557 100%)',
                color: '#fff',
                fontSize: 10.5,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                letterSpacing: '0.02em',
                flexShrink: 0,
                transition: 'opacity 0.15s, transform 0.15s',
                '&:hover': { opacity: 0.85, transform: 'scale(0.95)' },
              }}
            >
              {user.avatarInitials}
            </Box>

            {avatarOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: 0,
                  minWidth: 190,
                  background: 'rgba(255,255,255,0.97)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-lg)',
                  overflow: 'hidden',
                  zIndex: 200,
                  py: 0.75,
                }}
              >
                {/* User info */}
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.875,
                    borderBottom: '1px solid var(--color-border)',
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      lineHeight: 1.3,
                    }}
                  >
                    {user.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mt: '2px' }}>
                    {ROLE_LABELS[user.role]}
                  </Typography>
                </Box>

                {/* Switch user — только для демо-аккаунтов (is_demo) */}
                {user.isDemo && (
                  <>
                    <Box
                      sx={{
                        px: 1.5,
                        pt: 0.25,
                        pb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: 'var(--color-text-muted)',
                          opacity: 0.7,
                          mb: 0.5,
                        }}
                      >
                        Сменить пользователя
                      </Typography>
                    </Box>
                    {SWITCHABLE_USERS.map((u) => {
                      const isCurrent = u.role === user.role;
                      const isLoading = switchingRole === u.role;
                      const disabled = isCurrent || switchingRole !== null;
                      return (
                        <Box
                          key={u.role}
                          onClick={disabled ? undefined : () => void handleSwitch(u.role)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            px: 1.5,
                            py: 0.75,
                            cursor: disabled ? (isCurrent ? 'default' : 'not-allowed') : 'pointer',
                            opacity: switchingRole && !isLoading ? 0.5 : 1,
                            transition: 'background 0.12s',
                            '&:hover': disabled ? {} : { bgcolor: 'rgba(20,116,255,0.06)' },
                          }}
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              background: isCurrent
                                ? 'linear-gradient(135deg, #07111F 0%, #1D3557 100%)'
                                : 'linear-gradient(135deg, #1474FF 0%, #3B8BFF 100%)',
                              color: '#fff',
                              fontSize: 9,
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {u.initials}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: 'var(--color-text-primary)',
                                lineHeight: 1.3,
                              }}
                            >
                              {u.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 10.5,
                                color: 'var(--color-text-muted)',
                                lineHeight: 1.3,
                              }}
                            >
                              {ROLE_LABELS[u.role]}
                            </Typography>
                          </Box>
                          {isLoading ? (
                            <CircularProgress size={12} sx={{ color: 'var(--color-primary)' }} />
                          ) : isCurrent ? (
                            <Check size={13} style={{ color: 'var(--color-primary)' }} />
                          ) : null}
                        </Box>
                      );
                    })}

                    <Box
                      sx={{
                        height: '1px',
                        bgcolor: 'var(--color-border)',
                        my: 0.5,
                        mx: 1,
                      }}
                    />
                  </>
                )}

                {/* Logout */}
                <Box
                  onClick={() => {
                    setAvatarOpen(false);
                    logout();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.875,
                    cursor: 'pointer',
                    color: 'var(--color-text-secondary)',
                    fontSize: 12.5,
                    fontWeight: 500,
                    transition: 'background 0.12s',
                    '&:hover': { bgcolor: 'rgba(239,68,68,0.06)', color: '#ef4444' },
                  }}
                >
                  <LogOut size={13} />
                  Выйти
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
